(() => {
  if (customElements.get('cynn-cart-personalization')) return;
  const editors = () => [...document.querySelectorAll('cynn-cart-personalization')];
  const syncCheckout = () => {
    const pending = editors().some(editor => editor.dirty || editor.busy);
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(button => {
      if (pending && !button.disabled) {
        button.dataset.cynnEditDisabled = 'true';
        button.disabled = true;
      } else if (!pending && button.dataset.cynnEditDisabled) {
        delete button.dataset.cynnEditDisabled;
        button.disabled = false;
      }
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach(container => {
      if (pending && !container.inert) {
        container.dataset.cynnEditInert = 'true';
        container.inert = true;
      } else if (!pending && container.dataset.cynnEditInert) {
        delete container.dataset.cynnEditInert;
        container.inert = false;
      }
    });
    document.querySelectorAll('[data-cynn-edit-pending]').forEach(message => { message.hidden = !pending; });
  };
  class CynnCartPersonalization extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.details = this.querySelector('details');
      this.names = this.querySelector('[data-edit-names]');
      this.quantity = this.querySelector('[data-edit-quantity]');
      this.status = this.querySelector('[data-edit-status]');
      this.saveButton = this.querySelector('[data-edit-save]');
      this.cancelButton = this.querySelector('[data-edit-cancel]');
      this.originalNames = this.names.value;
      this.originalQuantity = this.quantity.value;
      this.hidden = false;
      this.addEventListener('change', event => event.stopPropagation());
      this.addEventListener('input', () => {
        this.names.removeAttribute('aria-invalid');
        this.paint();
        syncCheckout();
      });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open && this.dirty) {
          this.details.open = true;
          this.status.textContent = this.dataset.pendingMessage;
        } else if (this.details.open) this.paint();
      });
      this.saveButton.addEventListener('click', () => this.save());
      this.cancelButton.addEventListener('click', () => this.cancel());
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.cancel();
        } else if (event.key === 'Enter' && event.target === this.quantity) {
          event.preventDefault();
          this.save();
        }
      });
      syncCheckout();
    }
    disconnectedCallback() { syncCheckout(); }
    get dirty() {
      return this.initialized && (this.names.value !== this.originalNames || this.quantity.value !== this.originalQuantity);
    }
    state() {
      const text = this.names.value.trim();
      const count = text.split(/[,;\n\r]+/).map(name => name.trim()).filter(Boolean).length;
      const quantity = Number(this.quantity.value);
      const blank = this.dataset.allowBlank === 'true' && text.toLowerCase() === 'blanco';
      return { text, count, quantity, validQuantity: Number.isInteger(quantity) && quantity > 0 && this.quantity.checkValidity(), validNames: count > 0 && (blank || count === quantity) };
    }
    paint() {
      const state = this.state();
      this.status.classList.remove('is-error');
      this.status.textContent = this.dataset.countMessage.replace('__NAMES__', state.count).replace('__QTY__', state.quantity);
    }
    cancel() {
      if (this.busy) return;
      this.names.value = this.originalNames;
      this.quantity.value = this.originalQuantity;
      this.names.removeAttribute('aria-invalid');
      this.details.open = false;
      this.paint();
      syncCheckout();
      this.querySelector('summary').focus();
    }
    async save() {
      if (this.busy) return;
      const other = editors().find(editor => editor !== this && (editor.dirty || editor.busy));
      if (other) {
        other.details.open = true;
        other.status.textContent = other.dataset.pendingMessage;
        other.names.focus();
        return;
      }
      const state = this.state();
      if (!state.validQuantity || !state.validNames) {
        this.status.textContent = !state.validQuantity ? this.dataset.quantityMessage : this.dataset.mismatchMessage;
        this.status.classList.add('is-error');
        const target = !state.validQuantity ? this.quantity : this.names;
        target.setAttribute('aria-invalid', 'true');
        target.focus();
        return;
      }
      this.busy = true;
      this.saveButton.disabled = true;
      this.cancelButton.disabled = true;
      this.names.readOnly = true;
      this.quantityWasReadOnly = this.quantity.readOnly;
      this.quantity.readOnly = true;
      this.setAttribute('aria-busy', 'true');
      this.status.classList.remove('is-error');
      this.status.textContent = this.dataset.savingMessage;
      syncCheckout();
      try {
        const root = window.Shopify.routes.root;
        const response = await fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.dataset.errorMessage);
        const cart = await response.json();
        const line = cart.items.find(item => item.key === this.dataset.lineKey);
        if (!line || line.quantity !== Number(this.originalQuantity) || (line.properties?.[this.dataset.field] || '') !== this.originalNames) {
          throw new Error(this.dataset.staleMessage);
        }
        const properties = { ...line.properties, [this.dataset.field]: state.text };
        if (!properties._cynn_name_batch) properties._cynn_name_batch = crypto.randomUUID();
        const updatedResponse = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: state.quantity, properties })
        });
        const updated = await updatedResponse.json();
        if (!updatedResponse.ok || updated.errors || updated.status) {
          throw new Error(typeof updated.description === 'string' ? updated.description : this.dataset.errorMessage);
        }
        const saved = updated.items.some(item => item.variant_id === line.variant_id && item.quantity === state.quantity && Object.entries(properties).every(([key, value]) => item.properties?.[key] === value));
        if (!saved) throw new Error(this.dataset.errorMessage);
        window.location.reload();
      } catch (error) {
        this.busy = false;
        this.saveButton.disabled = false;
        this.cancelButton.disabled = false;
        this.names.readOnly = false;
        this.quantity.readOnly = this.quantityWasReadOnly;
        this.removeAttribute('aria-busy');
        this.status.textContent = error.message || this.dataset.errorMessage;
        this.status.classList.add('is-error');
        syncCheckout();
      }
    }
  }
  customElements.define('cynn-cart-personalization', CynnCartPersonalization);
  document.addEventListener('submit', event => {
    if (event.target.id !== 'cart') return;
    const pending = editors().find(editor => editor.dirty || editor.busy);
    if (!pending) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pending.details.open = true;
    pending.status.textContent = pending.dataset.pendingMessage;
    pending.names.focus();
  }, true);
})();
